import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminListCityHits, type CityHitRow, type CityHitSummary } from "@/lib/city-hits.functions";
import { AlertTriangle, ExternalLink, Globe, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/city-hits")({
  head: () => ({
    meta: [
      { title: "Admin — Stadspagina-bezoeken" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CityHitsPage,
});

function fmt(ts: string | null) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "medium" });
}

function statusBadge(status: number) {
  if (status === 200) {
    return (
      <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        200
      </span>
    );
  }
  if (status === 301) {
    return (
      <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        301
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
      {status}
    </span>
  );
}

function hostBadge(host: string) {
  const isWww = host.startsWith("www.");
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium " +
        (isWww ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-700")
      }
    >
      <Globe className="h-3 w-3" />
      {host}
    </span>
  );
}

function CityHitsPage() {
  const fetchHits = useServerFn(adminListCityHits);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "city-hits"],
    queryFn: () => fetchHits(),
    refetchInterval: 30_000,
  });

  const recent = (data?.recent ?? []) as CityHitRow[];
  const summary = (data?.summary ?? []) as CityHitSummary[];
  const windowDays = data?.windowDays ?? 7;

  // Highlight: stadspagina's waar www-variant is opgepikt (mogelijke indexatie
  // van verkeerde variant door Google).
  const wwwHits = summary.filter((s) => s.host.startsWith("www."));

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Stadspagina-bezoeken</h1>
          <p className="text-sm text-muted-foreground">
            Live logboek van SSR-requests naar <code>/groepenkast-vervangen/*</code>. 301 = www-variant
            (Google zou dit niet moeten indexeren), 200 = canonical non-www. Ververst automatisch elke 30s.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className={"h-4 w-4 " + (isFetching ? "animate-spin" : "")} />
          Ververs
        </button>
      </div>

      {isLoading && (
        <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">Laden…</div>
      )}

      {error && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <div>Kon bezoeken niet laden: {String((error as Error).message)}</div>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {wwwHits.length > 0 && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-semibold">
                  www-variant opgepikt bij {wwwHits.length} combinatie{wwwHits.length === 1 ? "" : "s"}
                </span>
              </div>
              <p className="mt-1 text-xs text-amber-800">
                Deze requests worden nu 301-doorgestuurd naar de non-www canonical. Controleer in
                Search Console of Google de juiste (non-www) URL indexeert.
              </p>
            </div>
          )}

          <section className="mb-8">
            <h2 className="mb-3 text-lg font-semibold">Samenvatting (laatste {windowDays} dagen)</h2>
            <div className="overflow-x-auto rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-medium">Stad</th>
                    <th className="px-4 py-2 font-medium">Host</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium text-right">Aantal</th>
                    <th className="px-4 py-2 font-medium">Laatste hit</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                        Nog geen bezoeken geregistreerd.
                      </td>
                    </tr>
                  )}
                  {summary.map((row) => (
                    <tr
                      key={`${row.stad}|${row.host}|${row.status}`}
                      className="border-t hover:bg-muted/30"
                    >
                      <td className="px-4 py-2 font-medium">{row.stad}</td>
                      <td className="px-4 py-2">{hostBadge(row.host)}</td>
                      <td className="px-4 py-2">{statusBadge(row.status)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.count}</td>
                      <td className="px-4 py-2 text-muted-foreground">{fmt(row.last_hit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold">Laatste 200 bezoeken</h2>
            <div className="overflow-x-auto rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-medium">Tijdstip</th>
                    <th className="px-4 py-2 font-medium">Stad</th>
                    <th className="px-4 py-2 font-medium">Host</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Referrer</th>
                    <th className="px-4 py-2 font-medium">User-Agent</th>
                    <th className="px-4 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                        Nog geen bezoeken.
                      </td>
                    </tr>
                  )}
                  {recent.map((row) => (
                    <tr key={row.id} className="border-t hover:bg-muted/30">
                      <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                        {fmt(row.hit_at)}
                      </td>
                      <td className="px-4 py-2 font-medium">{row.stad}</td>
                      <td className="px-4 py-2">{hostBadge(row.host)}</td>
                      <td className="px-4 py-2">{statusBadge(row.status)}</td>
                      <td className="px-4 py-2 max-w-[220px] truncate text-muted-foreground" title={row.referrer ?? ""}>
                        {row.referrer ?? "—"}
                      </td>
                      <td className="px-4 py-2 max-w-[260px] truncate text-muted-foreground" title={row.user_agent ?? ""}>
                        {row.user_agent ?? "—"}
                      </td>
                      <td className="px-4 py-2">
                        <a
                          href={`https://${row.host}${row.path}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          Open <ExternalLink className="h-3 w-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
